import { useEffect, useRef } from 'react';

import type { ThreadDetail } from '../types';
import type { MutableRefObject } from 'react';

/**
 * useMarkReadOnIncoming — marca a thread como lida quando chega mensagem de OUTRO
 * participante enquanto ela está ABERTA (N2-14, noturna 2 · cenário D5).
 *
 * Mecanismo do defeito: o mark-read só rodava ao abrir a thread. A mensagem nova
 * chegava pelo WS, o `useDirectMessagesWsSync` invalidava a query e a timeline
 * renderizava — mas `lastReadAt` do participante ficava ANTERIOR à mensagem que ele
 * já estava lendo na tela, e a lista à esquerda seguia «1 não lidas».
 *
 * Regras:
 *   - reage à MUDANÇA da última mensagem (id), não a re-renders nem à primeira
 *     carga — a abertura já tem o seu mark-read;
 *   - ignora a própria mensagem (o `reply` já invalidou; não há o que ler);
 *   - só marca com `document.visibilityState === 'visible'` — ler «no escuro»
 *     mentiria o `lastReadAt`; se a aba está oculta, adia e dispara no
 *     `visibilitychange` seguinte para visível.
 *
 * Reusa o MESMO mutate da abertura (via ref) — não duplica fetch nem invalidação.
 */
export function useMarkReadOnIncoming(
  threadId: string,
  thread: ThreadDetail | undefined,
  currentUserId: string | null,
  mutateReadRef: MutableRefObject<(threadId: string) => void>,
): void {
  const lastMessage = thread?.messages[thread.messages.length - 1];
  const lastMessageId = lastMessage?.messageId ?? null;
  const lastSenderId = lastMessage?.senderUserId ?? null;
  const loaded = thread !== undefined;

  // `undefined` = ainda não vimos a thread carregada (a primeira carga não conta).
  const seenLastIdRef = useRef<string | null | undefined>(undefined);
  const pendingReadRef = useRef(false);

  // Troca de thread: recomeça a observação (a abertura da nova já marca como lida).
  useEffect(() => {
    seenLastIdRef.current = undefined;
    pendingReadRef.current = false;
  }, [threadId]);

  useEffect(() => {
    if (!loaded) return;
    if (seenLastIdRef.current === undefined) {
      seenLastIdRef.current = lastMessageId;
      return;
    }
    if (seenLastIdRef.current === lastMessageId) return;
    seenLastIdRef.current = lastMessageId;

    if (lastSenderId === null || lastSenderId === currentUserId) return;

    if (document.visibilityState === 'visible') {
      mutateReadRef.current(threadId);
    } else {
      pendingReadRef.current = true;
    }
  }, [threadId, loaded, lastMessageId, lastSenderId, currentUserId, mutateReadRef]);

  useEffect(() => {
    const onVisibility = (): void => {
      if (document.visibilityState !== 'visible' || !pendingReadRef.current) return;
      pendingReadRef.current = false;
      mutateReadRef.current(threadId);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [threadId, mutateReadRef]);
}
